import { useState, useEffect } from "react";
import { format } from "date-fns";
import {
  Box,
  Stepper,
  Step,
  StepLabel,
  CardHeader,
  Dialog,
  Loading,
} from "../../../../../components";
import {
  OrcamentosService,
  OperacaoFiscalService,
  PessoaService,
  history,
  OrigemMercadoriaService,
  notification,
  UnidadeComercialService,
  ProdutosService,
  MeioPagamentoService,
} from "../../../../../services";
import {
  responseErros,
  formatDate,
  formatPrice,
  formatPrecisaoFloat,
} from "../../../../../utils";
import { OrcamentoStepperContent } from "..";
import { NfSaida, NfSaidaItem } from "../../../entities";
import {
  formatProdutosTotalizadoresDto,
  parcelaData,
  totalItensLista,
  totalVenda,
} from "../../../add-nf-saida/add-nf-saida";
import shortid from "shortid";

const ImportarOrcamentoDialog = ({
  openImportarOrcamentoDialog,
  setOpenImportarOrcamentoDialog,
  filtrosDialog,
  setFiltrosDialog,
}) => {
  const orcamentosService = new OrcamentosService();
  const operacaoFiscalService = new OperacaoFiscalService();
  const pessoaService = new PessoaService();
  const unidadeComercialService = new UnidadeComercialService();
  const origemMercadoriaService = new OrigemMercadoriaService();
  const produtosService = new ProdutosService();
  const meioPagamentoService = new MeioPagamentoService();

  const [loading, setLoading] = useState([]);
  const [activeStep, setActiveStep] = useState(0);
  const [orcamentos, setOrcamentos] = useState([]);
  const [orcamentosSelecionados, setOrcamentosSelecionados] = useState([]);
  const [orcamentoSelectionModel, setOrcamentoSelectionModel] = useState([]);
  const [itemSelectionModel, setItemSelectionModel] = useState([]);
  const [itensSelecionados, setItensSelecionados] = useState([]);
  const [totalListaOrcamentos, setTotalListaOrcamentos] = useState(0);
  const [unidadeList, setUnidadeList] = useState([]);
  const [origensMercadoriaList, setOrigensMercadoriaList] = useState([]);
  const [listaPessoas, setListaPessoas] = useState([]);
  const [listaVendedores, setListaVendedores] = useState([]);
  const [listaOperacoesFiscais, setListaOperacoesFiscais] = useState([]);
  const [listaMeiosPagamento, setListaMeiosPagamento] = useState([]);
  const [itemEditado, setItemEditado] = useState(false);
  const steps = ["Selecionar Orçamentos", "Selecionar Itens e Importar"];
  const colunas = [
    {
      field: "serieDocumento",
      headerName: "Série",
      width: 90,
    },
    {
      field: "numeroDocumento",
      headerName: "Nº Doc",
      width: 140,
    },
    {
      field: "dataEmissao",
      headerName: "Data de Emissão",
      width: 170,
      valueGetter: (params) =>
        format(formatDate.received(params.value), "dd/MM/yyyy"),
    },
    {
      field: "operacaoFiscalId",
      headerName: "Operação Fiscal",
      width: 250,
      valueGetter: (params) =>
        listaOperacoesFiscais.find((item) => item.id == params.value)
          ?.descricao,
    },
    {
      field: "pessoaClienteId",
      headerName: "Cliente",
      width: 170,
      valueGetter: (params) =>
        listaPessoas.find((item) => item.id == params.value)?.nomeRazaoSocial,
    },
    {
      field: "pessoaVendedorId",
      headerName: "Vendedor",
      width: 250,
      valueGetter: (params) =>
        listaVendedores.find((item) => item.id == params.value)
          ?.nomeRazaoSocial,
    },
    {
      field: "itens",
      headerName: "Total do Orçamento",
      width: 250,
      valueGetter: (params) => {
        const totalOrcamento =
          params.value
            .map((item) => item.valorUnitario * item.saldoExportar)
            .reduce((acumulador, total) => acumulador + total, 0) +
          params.row.acrescimo -
          params.row.desconto +
          params.row.despesas;
        return formatPrice(totalOrcamento);
      },
    },
  ];

  useEffect(() => {
    if (openImportarOrcamentoDialog) {
      buscarOrcamentos();
      buscarUnidadesComerciais();
      buscarOrigensMercadoria();
    }
  }, [openImportarOrcamentoDialog]);

  const buscarOrcamentos = async (filtro) => {
    setLoading(true);
    const filtros = {
      ...filtro,
      status: ["ABERTO", "PARCIALMENTE_FECHADO"],
    };
    const result = await orcamentosService.getAllFiltroAvancado(filtros);
    if (!result.isAxiosError) {
      setTotalListaOrcamentos(result.data.count);
      setOrcamentos(result.data.rows);
      buscarRelacionamentosOrcamento(result.data.rows);
    } else {
      responseErros(result);
    }
    setLoading(false);
  };

  const buscarOrcamentosAvancado = async (filtros) => {
    setLoading(true);
    const porIntervalo = [];
    const filtrosAvancadosTemp = {
      ...filtros,
      status: ["ABERTO", "PARCIALMENTE_FECHADO"],
      restritiva: true,
    };
    if (filtrosAvancadosTemp.dataInicial && filtrosAvancadosTemp.dataFinal) {
      porIntervalo.push({
        coluna: "dataEmissao",
        de: filtrosAvancadosTemp.dataInicial,
        ate: filtrosAvancadosTemp.dataFinal,
      });
    }
    delete filtrosAvancadosTemp.dataInicial;
    delete filtrosAvancadosTemp.dataFinal;
    const result = await orcamentosService.getAllFiltroAvancado({
      ...filtrosAvancadosTemp,
      porIntervalo,
    });
    if (!result.isAxiosError) {
      setOrcamentos(result.data.rows);
      setTotalListaOrcamentos(result.data.rows.length);
    } else {
      responseErros(result);
    }
    setLoading(false);
  };

  const buscarRelacionamentosOrcamento = (listaOrcamento) => {
    const idsPessoas = [];
    const idsOperacoesFiscais = [];
    const idsVendedores = [];
    const idsMeiosPagamento = [];
    for (const objeto of listaOrcamento) {
      if (objeto.pessoaClienteId) idsPessoas.push(objeto.pessoaClienteId);
      if (objeto.pessoaVendedorId) idsVendedores.push(objeto.pessoaVendedorId);
      if (objeto.meioPagamentoId)
        idsMeiosPagamento.push(objeto.meioPagamentoId);
    }
    buscarOperacoesFiscais([...new Set(idsOperacoesFiscais)]);
    buscarPessoas([...new Set(idsPessoas)]);
    buscarVendedores([...new Set(idsVendedores)]);
    buscarMeiosPagamento([...new Set(idsMeiosPagamento)]);
  };

  const buscarOperacoesFiscais = (listaIds) => {
    const filtro = {
      id: listaIds.length > 0 ? listaIds : undefined,
      nonPaginated: true,
    };
    operacaoFiscalService.getAll(filtro).then((result) => {
      if (!result.isAxiosError) {
        setListaOperacoesFiscais(result.data);
      } else {
        responseErros(result);
      }
    });
  };

  const buscarPessoas = (listaIds) => {
    if (listaIds.length > 0) {
      const filtro = {
        id: listaIds,
        nonPaginated: true,
        disabledInclude: true,
        attributes: ["id", "nomeRazaoSocial"],
      };
      pessoaService.getAll(filtro).then((result) => {
        if (!result.isAxiosError) {
          const listaPessoas = result.data;
          setListaPessoas(listaPessoas);
        } else {
          responseErros(result);
        }
      });
    }
  };

  const buscarMeiosPagamento = (listaIds) => {
    const filtro = {
      id: listaIds.length > 0 ? listaIds : undefined,
      nonPaginated: true,
    };
    meioPagamentoService.getAll(filtro).then((result) => {
      if (!result.isAxiosError) {
        setListaMeiosPagamento(result.data);
      } else {
        responseErros(result);
      }
    });
  };

  const buscarVendedores = (listaIds) => {
    const filtro = {
      id: listaIds.length > 0 ? listaIds : undefined,
      nonPaginated: true,
      vendedor: true,
      tiposPessoaId: 4,
      attributes: [
        "id",
        "nomeRazaoSocial",
        "email",
        "cnpjCpf",
        "inscricaoEstadual",
      ],
    };
    pessoaService.getAll(filtro).then((result) => {
      if (!result.isAxiosError) {
        setListaVendedores(result.data);
      } else {
        responseErros(result);
      }
    });
  };

  const handleDateChange = (date, fieldName) => {
    setFiltrosDialog((prevState) => ({
      ...prevState,
      [fieldName]: date != "Invalid Date" ? formatDate.toSend(date) : null,
    }));
  };

  const handleInputChange = (e) => {
    e.persist();
    setFiltrosDialog((prevState) => ({
      ...prevState,
      [e.target.name]: e.target.value,
    }));
  };

  const handleImportarOrcamento = async () => {
    if (!itemSelectionModel?.length)
      return notification.alertaGenericos(
        "Selecione ao menos um produto para importar",
      );
    window.nfSaidaEntity = await createNfSaida(
      orcamentosSelecionados[0],
      orcamentosSelecionados,
      itensSelecionados,
    );
    setOpenImportarOrcamentoDialog(false);
    resetarDadosImportacao();
    history.push("/faturamento/nf-saida/importar-orcamento");
  };

  const getOperacaoFiscal = async (id) => {
    if (!id) return;
    return operacaoFiscalService.getById(id).then((result) => {
      if (!result.isAxiosError) {
        return result.data;
      } else {
        responseErros(result);
        return null;
      }
    });
  };

  const buscarOrigensMercadoria = async () => {
    origemMercadoriaService.getAll().then((res) => {
      if (!res.isAxiosError) {
        setOrigensMercadoriaList(res.data);
      } else {
        responseErros(res);
      }
    });
  };

  const buscarUnidadesComerciais = async () => {
    const filtros = {
      nonPaginated: true,
    };
    unidadeComercialService.getAll(filtros).then((result) => {
      if (!result.isAxiosError) {
        setUnidadeList(result.data);
      } else {
        responseErros(result);
      }
    });
  };

  function recalcularProdutosAcrescimoPagamento(acrescimo, itens) {
    const calculoValorUnitario = (acrescimo, produto) =>
      acrescimo > 0
        ? formatPrecisaoFloat(
            produto?.valor + produto?.valor * (acrescimo / 100),
          )
        : produto?.valor;
    const calculoDesconto = (valor, produto) =>
      produto?.descontoPorcentagem
        ? (valor *
            produto?.quantidade *
            parseFloat(produto?.descontoPorcentagem)) /
          100
        : produto?.desconto;
    if (itens.length) {
      return itens.map((item) => {
        const valor = calculoValorUnitario(acrescimo, item);
        const desconto = calculoDesconto(valor, item);
        return {
          ...item,
          valor,
          desconto,
          subtotal: valor * item?.quantidade,
        };
      });
    }
  }
  const handleGerarParcelas = (
    valorTotalTitulo,
    quantidadeParcelas,
    orcamento,
    meioPagamentoSelecionado,
    condicaoSelecionada,
  ) => {
    const valorParcela = formatPrecisaoFloat(
      valorTotalTitulo / quantidadeParcelas,
    );
    const valorFormatado = formatPrecisaoFloat((valorParcela * 100) / 100);
    const restoDivisao = formatPrecisaoFloat(
      valorTotalTitulo - valorFormatado * quantidadeParcelas,
    );
    const parcelasGeradas = [];
    for (let contador = 1; contador <= quantidadeParcelas; contador++) {
      const valorTitulo =
        contador === quantidadeParcelas
          ? formatPrecisaoFloat(valorFormatado + parseFloat(restoDivisao))
          : valorFormatado;
      parcelasGeradas.push({
        id: shortid.generate(),
        numeroTitulo: contador,
        condicaoPagamentoId: orcamento?.condicaoPagamentoId,
        numeroAutorizacao: "",
        meioPagamentoId: orcamento?.meioPagamentoId,
        meioPagamentoSelecionado,
        valorTitulo,
        dataVencimento: parcelaData(
          contador,
          new Date(),
          condicaoSelecionada?.periodo,
        ),
      });
    }
    return parcelasGeradas;
  };

  const createNfSaida = async (orcamento, orcamentos, itens) => {
    const operacaoFiscal = await getOperacaoFiscal(orcamento.operacaoFiscalId);
    if (
      operacaoFiscal?.modeloDocumento == "055" ||
      operacaoFiscal?.modeloDocumento == "065"
    ) {
      return notification.alertaGenericos(
        "Operação fiscal sem regra de tributação associada.",
      );
    }
    const totalNota = totalVenda(orcamento, itens);
    const itensTemp = await buscarDadosProdutos(
      formatProdutosTotalizadoresDto(itens, totalItensLista(itens), orcamento),
    );
    let nfSaidaItens = itensTemp
      .filter((item) => itemSelectionModel.indexOf(item.id) > -1)
      .map((item, index) => {
        const nfSaidaItem = new NfSaidaItem(
          item,
          index,
          operacaoFiscal,
          origensMercadoriaList,
          unidadeList,
        );
        return {
          ...nfSaidaItem,
          produto: item.produto,
          valor: parseFloat(item.valorUnitario.toFixed(2)),
          subtotal:
            parseFloat(item.valorUnitario.toFixed(2)) * item.saldoExportar,
        };
      });
    let nfSaidaPagamentos = [];
    if (
      orcamento?.status === "ABERTO" &&
      itens?.length === itemSelectionModel?.length &&
      !itemEditado &&
      orcamentosSelecionados.length === 1
    ) {
      const meioPagamentoSelecionado = listaMeiosPagamento.find(
        (m) => m.id === orcamento.meioPagamentoId,
      );
      const condicaoSelecionada =
        meioPagamentoSelecionado?.condicoesPagamento.find(
          (c) => c.id === orcamento.condicaoPagamentoId,
        );
      const quantidadeParcelas = condicaoSelecionada?.parcelas;
      const itensRecalculados = recalcularProdutosAcrescimoPagamento(
        condicaoSelecionada.pagamentoInfo?.acrescimo,
        nfSaidaItens,
      );
      nfSaidaItens = itensRecalculados;
      nfSaidaPagamentos = handleGerarParcelas(
        totalNota,
        quantidadeParcelas,
        orcamento,
        meioPagamentoSelecionado,
        condicaoSelecionada,
      );
    }
    const informacoesAdicionais = orcamentos
      .map((pedido) => {
        return `Importado do orçamento ${pedido.serieDocumento}/${pedido.numeroDocumento}`;
      })
      .join("\n");
    let cliente = {};
    if (orcamento.pessoaClienteId) {
      cliente = listaPessoas.find(
        (item) => item.id == orcamento.pessoaClienteId,
      );
    }
    const dadosNfSaida = {
      dataEmissao: formatDate.toSend(new Date()),
      modalidadeFrete: "mfSemFrete",
      freteCompoeNota: false,
      orcamentoImport: true,
      informacoesAdicionais,
      orcamentoIds: orcamentos.map((orcamento) => orcamento.id),
      clienteId: orcamento?.pessoaClienteId ?? undefined,
      clienteEmail: cliente?.email,
      clienteCnpjCpf: cliente?.cnpjCpf,
      clienteNome: cliente?.nomeRazaoSocial,
      clienteInscricaoEstadual: cliente?.inscricaoEstadual ?? undefined,
      vendedorId: orcamento?.pessoaVendedorId ?? undefined,
      prestadorServicoId: orcamento.pessoaProficionalId,
      operacaoFiscal,
      operacaoFiscalId: operacaoFiscal?.id,
      serieDocumento: operacaoFiscal?.serieDocumento,
      modeloDocumento: operacaoFiscal?.modeloDocumento,
      meioPagamentoId: orcamento.meioPagamentoId,
      condicaoPagamentoId: orcamento.condicaoPagamentoId,
      desconto: orcamento.desconto ?? 0,
      acrescimo: orcamento.acrescimo ?? 0,
      despesas: orcamento.despesas ?? 0,
      nfSaidaItens,
      nfSaidaPagamentos,
    };
    return new NfSaida(dadosNfSaida, true);
  };

  async function buscarDadosProdutos(itens) {
    const listaIds = itens.map(({ produtoId }) => produtoId);
    const res = await produtosService.getAll({
      id: listaIds,
      nonPaginated: true,
    });
    if (!res.isAxiosError) {
      const produtosMap = new Map(
        res.data.map((produto) => [produto.id, produto]),
      );
      return itens.map((item) => ({
        ...item,
        ...produtosMap.get(item.produtoId),
        id: item.id,
      }));
    } else {
      responseErros(res);
    }
  }

  const resetarDadosImportacao = () => {
    setFiltrosDialog({});
    setOrcamentosSelecionados([]);
    setActiveStep(0);
  };

  const handleCancelarImportacao = () => {
    resetarDadosImportacao();
    setOpenImportarOrcamentoDialog(false);
  };

  const sendServerDatagrid = (props) => {
    if (props.tipoFiltro == "simples") {
      if (props.filtros?.status) {
        delete props.filtros.status;
      }
      buscarOrcamentos(props.filtros);
    } else {
      buscarOrcamentosAvancado(props.filtros);
    }
  };

  return (
    <Dialog fullWidth maxWidth="xl" open={openImportarOrcamentoDialog}>
      <Box sx={{ m: 2 }}>
        <CardHeader title="Importar Orçamento" />
      </Box>
      <Stepper activeStep={activeStep} alternativeLabel>
        {steps.map((label) => (
          <Step key={label}>
            <StepLabel>{label}</StepLabel>
          </Step>
        ))}
      </Stepper>
      <OrcamentoStepperContent
        sendServer={sendServerDatagrid}
        activeStep={activeStep}
        setActiveStep={setActiveStep}
        handleImportarOrcamento={handleImportarOrcamento}
        handleCancelarImportacao={handleCancelarImportacao}
        handleDateChange={handleDateChange}
        handleInputChange={handleInputChange}
        filtrosDialog={filtrosDialog}
        setFiltrosDialog={setFiltrosDialog}
        colunas={colunas}
        orcamentos={orcamentos}
        totalListaOrcamentos={totalListaOrcamentos}
        orcamentosSelecionados={orcamentosSelecionados}
        setOrcamentosSelecionados={setOrcamentosSelecionados}
        itensSelecionados={itensSelecionados}
        setItensSelecionados={setItensSelecionados}
        buscarOrcamentosAvancado={buscarOrcamentosAvancado}
        listaClientes={listaPessoas}
        itemSelectionModel={itemSelectionModel}
        setItemSelectionModel={setItemSelectionModel}
        orcamentoSelectionModel={orcamentoSelectionModel}
        setOrcamentoSelectionModel={setOrcamentoSelectionModel}
        setItemEditado={setItemEditado}
      />
      <Loading loading={loading} />
    </Dialog>
  );
};

export default ImportarOrcamentoDialog;
